import { EstimatesByTaxiServiceId, EstimatesPerVehicleType, FareEstimateResponseV4, TaxiFareEstimateResponseV2, VehicleType } from "../../../Services/FareEntities";
import { VehicleOption, TaxiServiceId } from "./ConditionEntities";
import { ConditionState } from "./ConditionState";
import { ServiceInfo, ServiceKind } from "../../../utils/service-vehicles/ServiceMetadata";

/** Mini-reducer for the ApplyFareEstimate action. It will transform the Conditions list by setting the Fares field. */
export function FareEstimateReducer(state: ConditionState, fares: FareEstimateResponseV4): ConditionState {

    const newSelectedCondition = UpdateVehicle(state.SelectedCondition, fares.Estimates, fares.EstimatesByServiceId);
    const newConditionList = UpdateVehicleList(state.ConditionList, fares.Estimates, fares.EstimatesByServiceId);

    return {
        ...state,
        SelectedCondition: newSelectedCondition,
        ConditionList: newConditionList,
        IsFixedFareAvailable: fares.IsFixedFareAvailable
    };
}

/**
 * Transform the Conditions list by setting the Fares field.
 * Existing Fare estimate V2 API provides a single fare value for the selected condition.
 * Temporarily, displaying same fare value for all the conditions in the Conditions list.
 */
export function FareEstimateReducerV2(state: ConditionState, fare: TaxiFareEstimateResponseV2): ConditionState {

    const taxiFare = fare.TotalFare.toString();

    const conditionListWithFare: VehicleOption[] = state.ConditionList.map((item) => {
        return {...item, Fare: taxiFare}
    });

    const selectedConditionWithFare = {...state.SelectedCondition, Fare: taxiFare};

    return {
        ...state,
        SelectedCondition: selectedConditionWithFare,
        ConditionList: conditionListWithFare
    }
}

/** Like UpdateVehicle, but for a whole array of VehiclePayload records. */
function UpdateVehicleList(vehicles: VehicleOption[], fares: EstimatesPerVehicleType, estimatesByServiceId?: EstimatesByTaxiServiceId): VehicleOption[] {
    let result: VehicleOption[] = [];

    for (let vehicle of vehicles) {
        result.push(UpdateVehicle(vehicle, fares, estimatesByServiceId));
    }

    return result;
}

/** Given a VehiclePayload with empty fare value, return a new VehiclePayload with the corresponding fare set. */
function UpdateVehicle(vehicle: VehicleOption, fares: EstimatesPerVehicleType, estimatesByServiceId?: EstimatesByTaxiServiceId): VehicleOption {
    
    // V2 API: Use direct 1:1 lookup by TaxiServiceId when available
    if (estimatesByServiceId && vehicle.ApiVehicle?.ApiVersion === 2) {
        const taxiServiceId = vehicle.ApiVehicle.ApiId;
        let fare = estimatesByServiceId[taxiServiceId];
        
        // Special fallback for Next Available when no direct pricing is available
        // Use pricing from similar vehicle types in priority order: Sedan -> Taxi -> Wagon
        if (!fare && taxiServiceId === TaxiServiceId.NextAvailable) {
            fare = estimatesByServiceId[TaxiServiceId.Sedan] 
                || estimatesByServiceId[TaxiServiceId.Taxi] 
                || estimatesByServiceId[TaxiServiceId.Wagon];
        }
        
        if (fare) {
            return { ...vehicle, FareDetails: fare };
        }
        // Fall through to legacy category-based lookup if no direct match
    }
    
    // V1 API (or fallback): Use legacy category-based lookup
    const vehicleType = GetVehicleTypeFromService(vehicle.Service);
    if (!vehicleType) return vehicle;

    let fare = fares[vehicleType];

    // sometimes 'Luxury' is null in the fare estimate response even though Silver Service is available in the area. We use Sedan estimates for Silver Service in this case.
    if ((vehicle.Service.kind === ServiceKind.SilverService || vehicle.Service.kind === ServiceKind.SilverServiceExec) && !fare) {
        fare = fares.Sedan;
    }

    if (!fare) return vehicle;

    const vehicleOption: VehicleOption = {
        ...vehicle,
        FareDetails: fare,
    };

    return vehicleOption;
}

/** Convert a Service (Kind) (which we get from Condition data) into a VehicleType (which Fare Estimates are expressed in). */
function GetVehicleTypeFromService(service: ServiceInfo): VehicleType | null {

    switch (service.kind) {
        case ServiceKind.MaxiTaxi:
            return "Maxi";

        case ServiceKind.SilverServiceExecXl:
            return "SilverServiceExecXl";

        /**
         * Mapping from ServiceKind.Taxi --> VehicleType.Sedan is added; which was requested by business requirement;
         * This is to fix the mapping from condition (named "Taxi") to fare (named "Sedan"), specific for SA/Adelaide.
         */
        case ServiceKind.Sedan:
        case ServiceKind.NextAvailable:
        case ServiceKind.NextAvailableSouthAustralia:
        case ServiceKind.Taxi:
        case ServiceKind.Parcel:
        case ServiceKind.Limo:
        case ServiceKind.StationWagon:
        case ServiceKind.Wheelchair:
            return "Sedan";

        case ServiceKind.SilverService:
            return "Luxury";

        case ServiceKind.SilverServiceExec:
            return "SilverServiceExec";

        default:
            return null; // the only safe thing is not to display an estimate
    }
}
